import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { GET_GPS_OFFICE_LOCATIONS } from '../../graphql/gps-settings';
import { UPDATE_MY_TEAM_STATUS } from '../../graphql/team-status';

const STATUS_THEME = {
  IN_OFFICE: {
    label: 'In Office',
    description: 'Working from one of the offices',
    base: 'bg-emerald-600 hover:bg-emerald-700',
    ring: 'focus-visible:ring-emerald-500',
  },
  OUT_IN_FIELD: {
    label: 'Out in the Field',
    description: 'On-site at a property or driving between jobs',
    base: 'bg-sky-600 hover:bg-sky-700',
    ring: 'focus-visible:ring-sky-500',
  },
  ENROUTE: {
    label: 'Enroute',
    description: 'On the way to a store or property',
    base: 'bg-amber-500 hover:bg-amber-600',
    ring: 'focus-visible:ring-amber-500',
  },
  OFF_ISLAND: {
    label: 'Off Island',
    description: 'Off the islands - not available for in-person work',
    base: 'bg-slate-600 hover:bg-slate-700',
    ring: 'focus-visible:ring-slate-500',
  },
  UNAVAILABLE: {
    label: 'Unavailable',
    description: 'Out sick / on break / cannot be assigned right now',
    base: 'bg-rose-600 hover:bg-rose-700',
    ring: 'focus-visible:ring-rose-500',
  },
};

const StatusButton = ({ theme, isCurrent, onClick, isSubmitting, label, sublabel }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isSubmitting}
      aria-label={`Set status to ${label}`}
      className={`relative w-full min-h-[64px] px-4 py-3 rounded-xl text-white text-left shadow-sm transition focus:outline-none focus-visible:ring-4 disabled:opacity-60 disabled:cursor-not-allowed ${theme.base} ${theme.ring}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="text-base font-semibold">{label}</div>
          {sublabel && (
            <div className="text-xs text-white/85 mt-0.5">{sublabel}</div>
          )}
        </div>
        {isCurrent && (
          <span className="bg-white/20 text-white text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full whitespace-nowrap">
            Current
          </span>
        )}
      </div>
    </button>
  );
};

const TeamStatusModal = ({
  isOpen,
  onClose,
  currentStatus,
  currentOfficeName,
}) => {
  const { data: officesData, loading: officesLoading } = useQuery(
    GET_GPS_OFFICE_LOCATIONS,
    { skip: !isOpen, fetchPolicy: 'cache-first' },
  );
  const [updateMyTeamStatus] = useMutation(UPDATE_MY_TEAM_STATUS, {
    refetchQueries: ['MyTeamStatus'],
    awaitRefetchQueries: true,
  });
  const [submittingKey, setSubmittingKey] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const offices = useMemo(
    () => officesData?.getGpsOfficeLocations || [],
    [officesData],
  );

  if (!isOpen) return null;

  const handleSubmit = async (status, officeName) => {
    const key = officeName ? `${status}::${officeName}` : status;
    setSubmittingKey(key);
    try {
      await updateMyTeamStatus({
        variables: {
          input: {
            status,
            ...(officeName ? { officeName } : {}),
          },
        },
      });
      toast.success('Status updated');
      onClose();
    } catch (error) {
      toast.error(error?.message || 'Failed to update status');
    } finally {
      setSubmittingKey(null);
    }
  };

  const renderInOfficeRow = () => {
    const theme = STATUS_THEME.IN_OFFICE;
    if (officesLoading) {
      return (
        <div className="w-full min-h-[64px] flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 text-sm">
          Loading offices...
        </div>
      );
    }
    if (offices.length === 0) {
      return (
        <div className="w-full min-h-[64px] flex items-center justify-center rounded-xl bg-amber-50 text-amber-700 text-sm px-4 text-center">
          No offices configured. Ask an admin to add offices in Settings to use the In Office status.
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {offices.map((office) => {
          const isCurrent =
            currentStatus === 'IN_OFFICE' && currentOfficeName === office.name;
          const submitKey = `IN_OFFICE::${office.name}`;
          return (
            <StatusButton
              key={office.name}
              theme={theme}
              isCurrent={isCurrent}
              isSubmitting={submittingKey !== null}
              label={office.name}
              sublabel="In Office"
              onClick={() => handleSubmit('IN_OFFICE', office.name)}
            />
          );
        })}
      </div>
    );
  };

  const renderSimpleRow = (statusKey) => {
    const theme = STATUS_THEME[statusKey];
    return (
      <StatusButton
        key={statusKey}
        theme={theme}
        isCurrent={currentStatus === statusKey}
        isSubmitting={submittingKey !== null}
        label={theme.label}
        sublabel={theme.description}
        onClick={() => handleSubmit(statusKey)}
      />
    );
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Set your status"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md max-h-[92vh] bg-white rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Set Your Status</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Tap a status. GPS from work orders updates this automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close status picker"
            className="p-2 -mr-1 rounded-lg text-gray-500 hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-3">
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              In Office
            </div>
            {renderInOfficeRow()}
          </div>

          <div className="space-y-2 pt-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              In the Field
            </div>
            {renderSimpleRow('OUT_IN_FIELD')}
            {renderSimpleRow('ENROUTE')}
          </div>

          <div className="space-y-2 pt-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Not Working
            </div>
            {renderSimpleRow('OFF_ISLAND')}
            {renderSimpleRow('UNAVAILABLE')}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex justify-end safe-bottom">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamStatusModal;
